import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';

import { IDynamicFormWidgetSection } from '@components/dynamic-form/types';
import { normalizeDynamicFormDrawerValues } from '@components/dynamic-form/utils';
import ToastErrorMessage from '@components/toast/ToastErrorMessage';
import FormattingService from '@core/services/FormattingService';
import { DEFAULT_ERROR_TEXT } from '@utils';
import { downloadExcelFile } from '@utils/excel/download-json-excel-service';

import {
  IBaseDrawerProps,
  IExportBusinessPartnersFilters
} from '@common/types';
import DynamicFormWidget from '@components/dynamic-form/DynamicFormWidget';
import { formatForFileName, normalizeMulti } from './exportReinsurers.drawer';
import {
  ALL,
  exportBusinessPartnersDrawerSections,
  exportBusinessPartnersExcelColumnWidths,
  exportBusinessPartnersExcelHeaders,
  exportBusinessPartnersExcelTemplateStyles
} from '@common/constants/drawer.constants';
import { RootState } from '@core/redux/store';
import { useSelector } from 'react-redux';
import {
  useGetBusinessPartners,
  useGetBusinessPartnersExportLovs
} from '@common/slices';
import {
  GetBusinessPartnersListResponse,
  GetBusinessPartnersListVariables
} from '@features/salesforce/business-partner';

export const buildLovs = (lovData: any) => {
  const companies = lovData?.salesforce?.getRelatedCompanies || [];

  const statuses =
    lovData?.BusinessPartnerStatuses?.enumValues?.map((e: any) => e.name) || [];
  const types =
    lovData?.BusinessPartnerTypes?.enumValues?.map((e: any) => e.name) || [];

  const RelatedCompanies: Record<string, string> = { [ALL]: 'All' };
  companies.forEach((c: any) => {
    if (c?.id) RelatedCompanies[String(c.id)] = String(c.title ?? c.id);
  });

  const BusinessPartnerStatuses: Record<string, string> = {
    [ALL]: 'All'
  };
  statuses.forEach((v: string) => {
    BusinessPartnerStatuses[v] = FormattingService.formatEnumToDisplay(v) ?? v;
  });

  const BusinessPartnerTypes: Record<string, string> = { [ALL]: 'All' };
  types.forEach((v: string) => {
    BusinessPartnerTypes[v] = FormattingService.formatEnumToDisplay(v) ?? v;
  });

  const currencies = lovData?.core?.getCurrencies || [];

  const Currency: Record<string, string> = {};
  currencies.forEach((c: any) => {
    const title = String(c.title ?? '');
    if (!title) return;
    if (c.id) Currency[String(c.id)] = title;
  });

  return {
    RelatedCompanies,
    BusinessPartnerStatuses,
    BusinessPartnerTypes,
    Currency
  };
};

const fetchAllBusinessPartners = async (
  fetchBusinessPartners: (options: {
    variables: GetBusinessPartnersListVariables;
  }) => Promise<{ data: GetBusinessPartnersListResponse }>,
  vars: {
    keyword: string | null;
    orderBy: any | null;
    companies: string[] | null;
    types: string[] | null;
    statuses: string[] | null;
    isLocal: boolean | null;
    isCompany: boolean | null;
    isRegistered: boolean | null;
    createdOnFrom: any | null;
    createdOnTo: any | null;
  }
) => {
  const pageSize = 500;
  let pageNumber = 1;

  const all: any[] = [];
  let hasMore = true;

  while (hasMore) {
    const { data } = await fetchBusinessPartners({
      variables: {
        keyword: vars.keyword,
        orderBy: vars.orderBy,
        companies: vars.companies,
        types: vars.types,
        statuses: vars.statuses,
        isLocal: vars.isLocal,
        isCompany: vars.isCompany,
        isRegistered: vars.isRegistered,
        createdOnFrom: vars.createdOnFrom,
        createdOnTo: vars.createdOnTo,
        pagination: { pageSize, pageNumber }
      }
    });

    const items = data?.salesforce?.searchBusinessPartners?.items || [];
    all.push(...items);

    hasMore = items.length === pageSize;
    pageNumber += 1;
  }

  return all;
};
const pickValue = (x: any): string => {
  if (x == null) return '';
  if (typeof x === 'string' || typeof x === 'number') return String(x);

  // multiSelect sometimes returns option objects
  if (typeof x === 'object') {
    return String(x.value ?? x.id ?? x.code ?? x.name ?? x.key ?? '');
  }

  return String(x);
};
const yesNo = (v: any) => (v === true ? 'Yes' : v === false ? 'No' : '');
const mapItemsToExcelData = (
  items: any[],
  lovs: Record<string, Record<string, string>>
) => {
  return items
    .map((bp: any) => {
      const relatedCompanyKey = pickValue(
        bp?.relatedCompany?.id ?? bp?.relatedCompany?.code ?? bp?.relatedCompany
      );

      const relatedCompanyTitle =
        bp?.relatedCompany?.title ??
        (relatedCompanyKey
          ? lovs?.RelatedCompanies?.[relatedCompanyKey]
          : '') ??
        relatedCompanyKey;

      const currencyKey = pickValue(bp?.maxCreditLimitAllowedCurrency);

      const currencyTitle =
        bp?.maxCreditLimitAllowedCurrency?.title ??
        (currencyKey ? lovs?.Currency?.[currencyKey] : '') ??
        '';

      return {
        personCode: bp?.person?.code ?? '',
        code: bp?.code ?? '',
        fullName: bp?.fullName ?? '',
        type: FormattingService.formatEnumToDisplay(bp?.type) ?? bp?.type ?? '',
        relatedCompany: relatedCompanyTitle ?? '',
        isCompany: yesNo(bp?.isCompany),
        isLocal: yesNo(bp?.isLocal),
        isRegistered: yesNo(bp?.isRegistered),
        registrationNumber: bp?.registerationNumber ?? '',
        registrationExpiryDate: bp?.registerationExpiryDate ?? '',
        maxCreditLimitAllowedCurrency: currencyTitle,
        maxCreditLimitAllowed: bp?.maxCreditLimitAllowed ?? '',
        status:
          FormattingService.formatEnumToDisplay(bp?.status) ?? bp?.status ?? ''
      };
    })
    .sort((a: any, b: any) =>
      String(a.code || '').localeCompare(String(b.code || ''))
    );
};

const ExportBusinessPartnersDrawer: React.FC<IBaseDrawerProps> = ({
  onSuccess
}) => {
  const submitButtonState = useSelector(
    (state: RootState) => state.apiState.buttonState
  );
  const [values, setValues] = useState<IExportBusinessPartnersFilters>({
    businessPartnerType: [ALL],
    businessPartnerStatus: [ALL],
    businessPartnerCreatedOn: []
  });

  const [lovs, setLovs] = useState<Record<string, Record<string, string>>>({});
  const [sections, setSections] = useState<IDynamicFormWidgetSection[]>([]);
  const { data: lovData } = useGetBusinessPartnersExportLovs.useQuery();
  const [fetchBusinessPartners] = useGetBusinessPartners.useLazy();
  useEffect(() => {
    setLovs({});
    setSections([]);
    setValues({
      businessPartnerType: [ALL],
      businessPartnerStatus: [ALL],
      businessPartnerCreatedOn: []
    });

    if (!lovData) return;

    const builtLovs = buildLovs(lovData);
    setLovs(builtLovs);

    setSections(exportBusinessPartnersDrawerSections(values, builtLovs));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lovData]);

  const submitForm = async (formValues: Record<string, any>) => {
    const [data] = normalizeDynamicFormDrawerValues(sections, formValues);
    try {
      const selectedCompanies = ['41'];
      const selectedTypes = normalizeMulti(data.businessPartnerType);
      const selectedStatuses = normalizeMulti(data.businessPartnerStatus);

      const [from, to] = data.businessPartnerCreatedOn || [];

      const createdOnFrom = from ? new Date(from).toISOString() : null;

      // inclusive end date by sending next day
      const createdOnTo = to
        ? new Date(
            new Date(to).setDate(new Date(to).getDate() + 1)
          ).toISOString()
        : null;

      const companiesVar = selectedCompanies.includes(ALL)
        ? null
        : selectedCompanies;

      const typesVar = selectedTypes.includes(ALL)
        ? null
        : selectedTypes.map(FormattingService.formatEnumToBackend);

      const statusesVar = selectedStatuses.includes(ALL)
        ? null
        : selectedStatuses.map(FormattingService.formatEnumToBackend);

      const pickerFrom = data.businessPartnerCreatedOn?.[0];
      const pickerTo = data.businessPartnerCreatedOn?.[1];

      let fileName = 'Redbridge_BusinessPartners';
      if (pickerFrom && pickerTo) {
        fileName += `_${formatForFileName(pickerFrom)}_${formatForFileName(
          pickerTo
        )}`;
      }

      const items = await fetchAllBusinessPartners(fetchBusinessPartners, {
        keyword: null,
        orderBy: null,
        companies: companiesVar,
        types: typesVar,
        statuses: statusesVar,
        isLocal: null,
        isCompany: null,
        isRegistered: null,
        createdOnFrom,
        createdOnTo
      });

      if (!items.length) {
        toast.error(
          <ToastErrorMessage>
            No business partners found for the selected filters
          </ToastErrorMessage>
        );

        return;
      }

      const excelData = mapItemsToExcelData(items, lovs);

      downloadExcelFile({
        data: excelData,
        filename: fileName,
        headers: exportBusinessPartnersExcelHeaders,
        sheetName: 'Sheet1',
        columnWidths: exportBusinessPartnersExcelColumnWidths,
        styles: exportBusinessPartnersExcelTemplateStyles,
        freezeHeaderRows: 1
      });

      onSuccess();
    } catch (e) {
      console.error(e);
      toast.error(<ToastErrorMessage>{DEFAULT_ERROR_TEXT}</ToastErrorMessage>);
    }
  };

  const handleChange = (
    fieldName: keyof IExportBusinessPartnersFilters,
    value: any,
    sectionIndex: number,
    updatedValues: IExportBusinessPartnersFilters
  ) => {
    const newValues = { ...updatedValues, [fieldName]: value };
    setValues(newValues);
    setSections(exportBusinessPartnersDrawerSections(newValues, lovs));
  };

  return (
    <DynamicFormWidget
      sections={sections}
      onSubmit={submitForm}
      submitButtonState={submitButtonState}
      onChange={handleChange}
    />
  );
};

export { ExportBusinessPartnersDrawer };
